import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Fragment, useMemo, useState } from 'react';
import { Box, Row, TextButton, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import AddFilter from '~/components/TableFilters/AddFilter/AddFilter';
import { recurrencePeriodsTextsPath } from '~/constants';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { deriveNameFilterOptions } from '../../AgreementsDashboard.utils';
import { DEFAULT_AGREEMENT_FILTER, isDefaultAgreementFilter } from '../agreementFilterDefaults';
import { useAgreementsAiSearchStore } from '../hooks/useAgreementsAiSearchStore';
import { ADDABLE_FILTER_GROUPS, ADDABLE_FILTER_KEYS, addEmptyFilter, isAddableFilterPresent } from './agreementAddFilter';
import { buildAgreementPredicateChipUnits, buildPredicateCallbacks } from './AgreementPredicateChips.utils';
export const GetAgreementPredicateChipsTID = (parentTestId)=>getTestIds(parentTestId);
const CHIP_KEY_BY_ADDABLE_FILTER = {
    status: 'status',
    paymentMethod: 'paymentMethod',
    amendmentStatus: 'amendmentStatus',
    hasCredit: 'hasCredit',
    automaticPriceIncrease: 'automaticPriceIncrease',
    effectiveDate: 'effectiveDate',
    lastBilled: 'lastBilled',
    occurrence: 'service-recurrence',
    automaticServices: 'service-automatic-state',
    serviceName: 'service-name',
    serviceStatus: 'service-status',
    billingTrigger: 'service-billing-trigger'
};
export const AgreementPredicateChips = (param)=>{
    let { testId, serviceNameOptions, agreements, isVendor, matchCount } = param;
    const { AgreementPredicateChipsTestIds } = useMemo(()=>GetAgreementPredicateChipsTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('agreement');
    const { t: commonFilter } = useLocaleKeys('common.filter');
    const { t: aiSearchTexts } = useLocaleKeys('pages.agreementsDashboard.aiSearch');
    const { t: recurrencePeriodsTexts } = useLocaleKeys(recurrencePeriodsTextsPath);
    const filter = useAgreementsAiSearchStore((s)=>s.filter);
    const editFilter = useAgreementsAiSearchStore((s)=>s.editFilter);
    const setFilter = useAgreementsAiSearchStore((s)=>s.setFilter);
    const [autoOpenKey, setAutoOpenKey] = useState(null);
    useOnMount(()=>{
        if (useAgreementsAiSearchStore.getState().filter == null) setFilter(DEFAULT_AGREEMENT_FILTER, '');
    });
    const nameOptions = useMemo(()=>deriveNameFilterOptions(agreements, isVendor), [
        agreements,
        isVendor
    ]);
    const activeFilter = filter !== null && filter !== void 0 ? filter : {
        version: '1'
    };
    const statusOptions = [
        {
            value: 'active',
            label: t.agreementStatusOptions.active()
        },
        {
            value: 'exhausted',
            label: t.agreementStatusOptions.completed()
        },
        {
            value: 'terminated',
            label: t.agreementStatusOptions.terminated()
        }
    ];
    const paymentMethodOptions = [
        {
            value: 'credit',
            label: t.paymentMethodOptions.card()
        },
        {
            value: 'bank',
            label: t.paymentMethodOptions.bankAccount()
        },
        {
            value: 'verificationInitiated',
            label: t.paymentMethodOptions.manualVerificationInitiated()
        },
        {
            value: 'verificationFailed',
            label: t.paymentMethodOptions.manualVerificationFailed()
        },
        {
            value: 'notSet',
            label: t.paymentMethodOptions.notSet()
        }
    ];
    const amendmentStatusOptions = [
        {
            value: 'amendmentDraft',
            label: t.amendmentsStatusOptions.amendmentDraft()
        },
        {
            value: 'awaitingApproval',
            label: t.amendmentsStatusOptions.awaitingApproval()
        },
        {
            value: 'noChanges',
            label: t.amendmentsStatusOptions.noChanges()
        }
    ];
    const hasCreditOptions = [
        {
            value: 'yes',
            label: t.hasCreditOptions.hasCredit()
        },
        {
            value: 'no',
            label: t.hasCreditOptions.none()
        }
    ];
    const priceIncreaseOptions = [
        {
            value: 'automatic',
            label: t.priceIncreaseOptions.included()
        },
        {
            value: 'none',
            label: t.priceIncreaseOptions.notIncluded()
        }
    ];
    const occurrenceOptions = [
        {
            value: 'oneTime',
            label: recurrencePeriodsTexts.oneTime()
        },
        {
            value: 'weekly',
            label: recurrencePeriodsTexts.weekly()
        },
        {
            value: 'biweekly',
            label: recurrencePeriodsTexts.everyTwoWeeks()
        },
        {
            value: 'monthly',
            label: recurrencePeriodsTexts.monthly()
        },
        {
            value: 'quarterly',
            label: recurrencePeriodsTexts.quarterly()
        },
        {
            value: 'yearly',
            label: recurrencePeriodsTexts.yearly()
        },
        {
            value: 'onDemand',
            label: recurrencePeriodsTexts.onDemand()
        }
    ];
    const automaticStateOptions = [
        {
            value: 'active',
            label: t.automaticOptions.active()
        },
        {
            value: 'paused',
            label: t.automaticOptions.paused()
        }
    ];
    const completionStateOptions = [
        {
            value: 'completed',
            label: t.completionOptions.completed()
        },
        {
            value: 'notCompleted',
            label: t.completionOptions.notCompleted()
        }
    ];
    const billingTriggerOptions = [
        {
            value: 'automatic',
            label: t.billingTriggerOptions.automatic()
        },
        {
            value: 'manual',
            label: t.billingTriggerOptions.manual()
        }
    ];
    const testIds = {
        Status: AgreementPredicateChipsTestIds.Status(),
        PaymentMethod: AgreementPredicateChipsTestIds.PaymentMethod(),
        AmendmentStatus: AgreementPredicateChipsTestIds.AmendmentStatus(),
        HasCredit: AgreementPredicateChipsTestIds.HasCredit(),
        PriceIncrease: AgreementPredicateChipsTestIds.PriceIncrease(),
        EffectiveDate: AgreementPredicateChipsTestIds.EffectiveDate(),
        ApprovalDate: AgreementPredicateChipsTestIds.ApprovalDate(),
        ClientName: AgreementPredicateChipsTestIds.ClientName(),
        AgreementTitle: AgreementPredicateChipsTestIds.AgreementTitle(),
        ContactName: AgreementPredicateChipsTestIds.ContactName(),
        Assignee: AgreementPredicateChipsTestIds.Assignee(),
        LastBilled: AgreementPredicateChipsTestIds.LastBilled(),
        Occurrence: AgreementPredicateChipsTestIds.Occurrence(),
        AutomaticServices: AgreementPredicateChipsTestIds.AutomaticServices(),
        ServiceName: AgreementPredicateChipsTestIds.ServiceName(),
        ServiceStatus: AgreementPredicateChipsTestIds.ServiceStatus(),
        BillingTrigger: AgreementPredicateChipsTestIds.BillingTrigger(),
        BundleName: AgreementPredicateChipsTestIds.BundleName(),
        OrGroup: AgreementPredicateChipsTestIds.OrGroup()
    };
    const callbacks = buildPredicateCallbacks(activeFilter, (next)=>editFilter(_object_spread({
            version: activeFilter.version
        }, next)));
    const texts = {
        labels: {
            status: t.filterLabels.agreementStatus(),
            paymentMethod: t.filterLabels.paymentMethod(),
            amendmentStatus: t.filterLabels.amendmentsStatus(),
            hasCredit: t.aiFilterLabels.hasCredit(),
            priceIncrease: t.filterLabels.servicePriceIncreaseState(),
            effectiveDate: t.filterLabels.effectiveDate(),
            approvalDate: t.filterLabels.approvalDate(),
            clientName: t.filterLabels.clientName(),
            agreementTitle: t.filterLabels.agreementTitle(),
            contactName: t.filterLabels.contactName(),
            assignee: t.filterLabels.assignee(),
            lastBilled: t.filterLabels.lastBillingDate(),
            occurrence: t.filterLabels.recurrence(),
            automaticServices: t.filterLabels.automaticServices(),
            serviceName: t.filterLabels.serviceNames(),
            serviceStatus: t.filterLabels.completion(),
            billingTrigger: t.filterLabels.billingTrigger(),
            bundleName: t.filterLabels.bundleNames()
        },
        options: {
            status: statusOptions,
            paymentMethod: paymentMethodOptions,
            amendmentStatus: amendmentStatusOptions,
            hasCredit: hasCreditOptions,
            priceIncrease: priceIncreaseOptions,
            occurrence: occurrenceOptions,
            automaticState: automaticStateOptions,
            completionState: completionStateOptions,
            billingTrigger: billingTriggerOptions,
            serviceName: serviceNameOptions,
            clientName: nameOptions.clientName,
            agreementTitle: nameOptions.agreementTitle,
            contactName: nameOptions.contactName,
            assignee: nameOptions.assignee
        },
        isNotText: ()=>t.aiChipOperator.isNot(),
        hasNoText: ()=>t.aiChipOperator.hasNo(),
        bundleNoun: ()=>t.bundlesNoun(),
        applyText: commonFilter.apply(),
        selectAllText: commonFilter.selectAll(),
        setValue: {
            all: t.aiChipValue.all(),
            count: (count)=>t.aiChipValue.count({
                    count
                })
        },
        dateRange: {
            allTime: t.dateRangeLabels.allTime(),
            specificRange: t.dateRangeLabels.specificRange(),
            from: t.dateRangeLabels.from(),
            to: t.dateRangeLabels.to()
        },
        lastBilled: {
            all: t.lastBillingDateOptions.all(),
            allChip: t.aiChipValue.all(),
            everBilled: t.lastBillingDateOptions.everBilled(),
            neverBilled: t.lastBillingDateOptions.neverBilled(),
            specificRange: t.lastBillingDateOptions.specificRange(),
            from: t.dateRangeLabels.from(),
            to: t.dateRangeLabels.to()
        }
    };
    const units = buildAgreementPredicateChipUnits({
        predicate: activeFilter,
        testIds,
        callbacks,
        texts,
        autoOpenKey
    });
    const addFilterLabels = {
        status: texts.labels.status,
        paymentMethod: texts.labels.paymentMethod,
        amendmentStatus: texts.labels.amendmentStatus,
        hasCredit: texts.labels.hasCredit,
        automaticPriceIncrease: texts.labels.priceIncrease,
        effectiveDate: texts.labels.effectiveDate,
        lastBilled: texts.labels.lastBilled,
        occurrence: texts.labels.occurrence,
        automaticServices: texts.labels.automaticServices,
        serviceName: texts.labels.serviceName,
        serviceStatus: texts.labels.serviceStatus,
        billingTrigger: texts.labels.billingTrigger
    };
    const groupLabels = {
        agreements: t.filterGroupLabels.agreements(),
        services: t.filterGroupLabels.services()
    };
    const addFilterGroups = ADDABLE_FILTER_GROUPS.map((group)=>({
            label: groupLabels[group.group],
            filters: [
                ...group.keys
            ]
        }));
    const addedKeys = ADDABLE_FILTER_KEYS.filter((key)=>isAddableFilterPresent(activeFilter, key));
    const hasAppliedFilter = !isDefaultAgreementFilter(activeFilter);
    return /*#__PURE__*/ _jsx(Box, {
        testId: AgreementPredicateChipsTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Row, {
            alignItems: "center",
            gap: 8,
            wrap: true,
            children: [
                units.map((unit)=>/*#__PURE__*/ _jsx(Fragment, {
                        children: unit.node
                    }, unit.key)),
                /*#__PURE__*/ _jsx(AddFilter, {
                    testId: AgreementPredicateChipsTestIds.AddFilter(),
                    filtersMap: addFilterLabels,
                    selectedFilters: addedKeys,
                    onSelect: (key)=>{
                        setAutoOpenKey(CHIP_KEY_BY_ADDABLE_FILTER[key]);
                        editFilter(addEmptyFilter(activeFilter, key));
                    },
                    groups: addFilterGroups
                }),
                hasAppliedFilter && /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 4,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: AgreementPredicateChipsTestIds.Count(),
                            skin: "secondary",
                            variant: "paragraph2",
                            children: aiSearchTexts.count({
                                count: matchCount
                            })
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            variant: "paragraph2",
                            children: "•"
                        }),
                        /*#__PURE__*/ _jsx(TextButton, {
                            testId: AgreementPredicateChipsTestIds.ClearButton(),
                            bi: {
                                name: 'agreements_ai_search_clear'
                            },
                            onClick: ()=>setFilter(DEFAULT_AGREEMENT_FILTER, ''),
                            variant: "paragraph2",
                            underline: "none",
                            weight: "normal",
                            children: aiSearchTexts.clearButton()
                        })
                    ]
                })
            ]
        })
    });
};
